import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import updatePlatformNumber from '@/API/PlatformNumber/updatePlatformNumber';
import { TEAMS_BULK_NUMBERS_KEY } from '../constants';
import { buildPayload } from '../utils/bulkUpdate';

// There is no bulk endpoint, so numbers are updated one request each, a few
// at a time.
export const CONCURRENCY = 4;

const FALLBACK_REASON = 'The update could not be completed';

export const IDLE_RUN = {
    status: 'idle',
    total: 0,
    processed: 0,
    success: 0,
    ignored: 0,
    failed: [],
    // Cancel was pressed: updates already sent are finishing
    isCancelling: false,
    // Numbers a cancelled run never sent (left unchanged)
    notSent: 0,
};

export const getFailureReason = (error) =>
    error?.response?.data?.message || error?.message || FALLBACK_REASON;

/**
 * Runs `worker` over `items` with at most `limit` in flight at once. Once
 * `shouldContinue` returns false no more items are started; ones already in
 * flight still finish.
 */
export const runWithConcurrency = async (
    items,
    limit,
    worker,
    shouldContinue = () => true,
) => {
    let nextIndex = 0;
    const lane = async () => {
        while (nextIndex < items.length && shouldContinue()) {
            const item = items[nextIndex];
            nextIndex += 1;
            await worker(item);
        }
    };
    await Promise.all(
        Array.from({ length: Math.min(limit, items.length) }, lane),
    );
};

/**
 * Applies a Bulk Update plan: one `PUT /PlatformNumber` per changing number.
 * Numbers with nothing to change are counted as ignored. Successful updates
 * are written into the cached numbers list so the new values show for the
 * rest of the session; failed numbers are left as they were.
 *
 * `cancel` stops the run early: updates already sent finish, the rest are
 * never sent and counted as `notSent`. Leaving the page cancels it too (a
 * refresh does the same, as the requests live in this page).
 */
const useBulkUpdateRunner = ({ scope }) => {
    const queryClient = useQueryClient();
    const [run, setRun] = useState(IDLE_RUN);
    const isMountedRef = useRef(true);
    const isCancelRequestedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const start = useCallback(
        async (items) => {
            const toUpdate = items.filter((item) => item.diff.length > 0);
            const ignored = items.length - toUpdate.length;
            const updated = new Map();
            const updateRun = (change) => {
                if (isMountedRef.current) setRun(change);
            };
            isCancelRequestedRef.current = false;

            setRun({
                ...IDLE_RUN,
                status: 'running',
                total: items.length,
                processed: ignored,
                ignored,
            });

            const worker = async (item) => {
                const payload = buildPayload(item.row, item.next);
                try {
                    const response = await updatePlatformNumber({
                        formData: payload,
                        scope,
                    });
                    updated.set(item.row.id, {
                        ...payload,
                        ...(response && typeof response === 'object'
                            ? response
                            : {}),
                    });
                    updateRun((current) => ({
                        ...current,
                        processed: current.processed + 1,
                        success: current.success + 1,
                    }));
                } catch (error) {
                    updateRun((current) => ({
                        ...current,
                        processed: current.processed + 1,
                        failed: [
                            ...current.failed,
                            {
                                id: item.row.id,
                                e164: item.row.e164,
                                reason: getFailureReason(error),
                            },
                        ],
                    }));
                }
            };
            await runWithConcurrency(
                toUpdate,
                CONCURRENCY,
                worker,
                () => isMountedRef.current && !isCancelRequestedRef.current,
            );

            // One cache write at the end, so the lists re-render once. Done
            // even after leaving the page, as those numbers did change.
            if (updated.size > 0) {
                queryClient.setQueriesData(
                    { queryKey: [TEAMS_BULK_NUMBERS_KEY] },
                    (data) =>
                        data?.platformNumbers
                            ? {
                                  ...data,
                                  platformNumbers: data.platformNumbers.map(
                                      (number) =>
                                          updated.has(number.numberID)
                                              ? {
                                                    ...number,
                                                    ...updated.get(
                                                        number.numberID,
                                                    ),
                                                }
                                              : number,
                                  ),
                              }
                            : data,
                );
            }

            updateRun((current) => ({
                ...current,
                status: 'done',
                isCancelling: false,
                notSent: current.total - current.processed,
            }));
        },
        [queryClient, scope],
    );

    const reset = useCallback(() => setRun(IDLE_RUN), []);

    const cancel = useCallback(() => {
        isCancelRequestedRef.current = true;
        setRun((current) =>
            current.status === 'running'
                ? { ...current, isCancelling: true }
                : current,
        );
    }, []);

    return { run, start, cancel, reset };
};

export default useBulkUpdateRunner;
